import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import NetworkImageUpload from '../components/networks/NetworkImageUpload';
import { useNetworks } from '../hooks/useNetworks';
import { Network } from '../services/firebaseFunctions';
import { useAuth } from '../contexts/AuthContext';
import { operationErrorMessage } from '../utils/adminFeedback';
import DestructiveActionDialog from '../components/DestructiveActionDialog';

const NetworksPage: React.FC = () => {
  const { networks, loading, error, create, update, remove } = useNetworks();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('redes', 'editar');
  const [editing, setEditing] = useState<Network | null>();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [feedback, setFeedback] = useState<string>();
  const [feedbackSeverity, setFeedbackSeverity] = useState<'success' | 'error'>('error');
  const [formError, setFormError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [networkPendingDeletion, setNetworkPendingDeletion] = useState<Network | null>(null);

  const openForm = (network?: Network) => {
    setEditing(network || null);
    setName(network?.name || '');
    setDescription(network?.description || '');
    setFormError(undefined);
    setOpen(true);
  };

  const save = async () => {
    if (savingRef.current) return;

    if (name.trim().length < 2) {
      setFormError('Informe um nome com ao menos 2 caracteres.');
      return;
    }
    setFormError(undefined);
    savingRef.current = true;
    setSaving(true);

    try {
      if (editing) {
        await update(editing.id, {
          name: name.trim(),
          description: description.trim() || null,
        });
      } else {
        const normalizedDescription = description.trim();
        await create({
          name: name.trim(),
          ...(normalizedDescription ? { description: normalizedDescription } : {}),
        });
      }
      setOpen(false);
    } catch (caught) {
      const duplicate = caught instanceof Error && caught.message.includes('already-exists');
      setFormError(duplicate ? 'Já existe uma Rede com este nome.' : operationErrorMessage('network'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const removeNetwork = async () => {
    if (!networkPendingDeletion) return;

    try {
      await remove(networkPendingDeletion.id);
      setFeedback('Rede excluída com sucesso. As Empresas vinculadas foram desvinculadas.');
      setFeedbackSeverity('success');
      setNetworkPendingDeletion(null);
    } catch {
      setFeedback(operationErrorMessage('network'));
      setFeedbackSeverity('error');
    }
  };

  return <Box>
    <Typography variant="h4" sx={{ mb: 3 }}>Redes</Typography>
    {(error || feedback) && <Alert severity={feedback ? feedbackSeverity : 'error'} sx={{ mb: 2 }}>{feedback || 'Não foi possível carregar as Redes.'}</Alert>}
    <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Redes.'}>
      <span>
        <Button variant="contained" startIcon={<Add />} onClick={() => openForm()} disabled={!canManage} sx={{ mb: 2 }}>Nova Rede</Button>
      </span>
    </Tooltip>
    <Paper>
      <Table>
        <TableHead><TableRow><TableCell>Nome</TableCell><TableCell>Descrição</TableCell><TableCell align="right">Ações</TableCell></TableRow></TableHead>
        <TableBody>
          {loading ? <TableRow><TableCell colSpan={3}>Carregando…</TableCell></TableRow>
            : networks.length === 0 ? <TableRow><TableCell colSpan={3}>Nenhuma Rede cadastrada.</TableCell></TableRow>
              : networks.map((network) => <TableRow key={network.id}>
                <TableCell>{network.name}</TableCell>
                <TableCell>{network.description || '—'}</TableCell>
                <TableCell align="right">
                  <IconButton aria-label="Ver rede" onClick={() => navigate(`/networks/${network.id}`)}><Visibility /></IconButton>
                  <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Redes.'}>
                    <span>
                      <IconButton aria-label="Editar rede" disabled={!canManage} onClick={() => openForm(network)}><Edit /></IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Redes.'}>
                    <span>
                      <IconButton aria-label="Excluir rede" color="error" disabled={!canManage} onClick={() => setNetworkPendingDeletion(network)}><Delete /></IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>)}
        </TableBody>
      </Table>
    </Paper>
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
      <DialogTitle>{editing ? 'Editar Rede' : 'Nova Rede'}</DialogTitle>
      <DialogContent>
        {formError && <Alert severity="error" sx={{ mt: 1 }}>{formError}</Alert>}
        <TextField
          autoFocus
          fullWidth
          required
          label="Nome"
          value={name}
          onChange={(event) => {
            setFormError(undefined);
            setName(event.target.value);
          }}
          margin="normal"
          error={Boolean(formError)}
          inputProps={{ maxLength: 100 }}
        />
        <TextField fullWidth label="Descrição" value={description} onChange={(event) => setDescription(event.target.value)} margin="normal" multiline minRows={3} inputProps={{ maxLength: 500 }} />
        {editing && <NetworkImageUpload network={editing} onChanged={setEditing} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={() => void save()} disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
    <DestructiveActionDialog
      open={Boolean(networkPendingDeletion)}
      entityLabel="Rede"
      entityName={networkPendingDeletion?.name || ''}
      onCancel={() => setNetworkPendingDeletion(null)}
      onConfirm={() => void removeNetwork()}
    />
  </Box>;
};

export default NetworksPage;
